<script setup>
// KISOFT: sidebar do painel de Gestao replicada VERBATIM (mesmos icones/cores/secoes/
// logo/dica/rodape do gestao_sidebar() do index.php). Vira a barra fixa dentro do Chatwoot.
// Emite 'select' com o item; o pai (Sidebar.vue) controla a view ativa e o "voltar ao Chatwoot".
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { getLastMessage } from 'dashboard/helper/conversationHelper';
const props = defineProps({
  view: { type: String, default: 'atendimento' },
  // recolhida = so icones (64px) AGORA (nao fixada e sem o mouse em cima); estado mora no Sidebar.vue
  recolhida: { type: Boolean, default: false },
  // fixada = fica aberta mesmo sem o mouse (pin ligado); salvo no navegador
  fixada: { type: Boolean, default: true },
});
const emit = defineEmits(['select', 'exit', 'toggle', 'abrir']);

// KISOFT: badge do Atendimento = quantas conversas estao na aba "Responder" (minhas, nao
// respondidas). Mesmo criterio do ChatList (mineChatsList.filter(isMineUnanswered)).
const mineChats = useMapGetter('getMineChats');
const isMineUnanswered = c => {
  const last = getLastMessage(c);
  if (last && Number(last.message_type) === 1 && (last.status === 'failed' || (last.private && /o foi enviada, pois o contato|enviar a mensagem. Verifique sua conex/.test(last.content || '')))) return true;
  return !c.first_reply_created_at || Number(c.waiting_since) > 0;
};
const responderCount = computed(() => {
  try {
    return mineChats
      .value({ assigneeType: 'me', status: 'open' })
      .filter(isMineUnanswered).length;
  } catch (e) {
    return 0;
  }
});

// KISOFT: badge do Calendario = tarefas do PROPRIO usuario, nao concluidas, ate hoje. Poll (sem refresh).
const currentUser = useMapGetter('getCurrentUser');
const calCount = ref(0);
let calTimer = null;
const pollCal = () => {
  const tok = (currentUser.value && currentUser.value.access_token) || '';
  if (!tok) return;
  fetch('https://kisoft.com.br/tarefass/index.php?tasks_hoje=1&cwtok=' + encodeURIComponent(tok), { credentials: 'include', cache: 'no-store' })
    .then(r => r.json())
    .then(d => { calCount.value = d && d.linked && d.count ? d.count : 0; })
    .catch(() => {});
};

// KISOFT: badge da Visao Executiva = indicadores ABAIXO da meta (igual ao painel).
// O painel grava esse numero quando a exec renderiza; aqui so lemos o cache dele.
const execBelow = ref(0);
const pollExec = () => {
  const tok = (currentUser.value && currentUser.value.access_token) || '';
  if (!tok) return;
  fetch('https://kisoft.com.br/tarefass/index.php?exec_badge=1&cwtok=' + encodeURIComponent(tok), { credentials: 'include', cache: 'no-store' })
    .then(r => r.json())
    .then(d => { execBelow.value = d && d.below ? Number(d.below) : 0; })
    .catch(() => {});
};

// KISOFT: paginas liberadas p/ ESTE usuario (config users.perms no painel). null = carregando.
// So mostra na sidebar o que ele pode acessar; Atendimento (Chatwoot) sempre aparece.
const allowedKeys = ref(null);
const loadPerms = () => {
  const tok = (currentUser.value && currentUser.value.access_token) || '';
  if (!tok) return;
  fetch('https://kisoft.com.br/tarefass/index.php?nav_perms=1&cwtok=' + encodeURIComponent(tok), { credentials: 'include', cache: 'no-store' })
    .then(r => r.json())
    .then(d => { allowedKeys.value = d && Array.isArray(d.keys) ? d.keys : []; })
    .catch(() => { allowedKeys.value = []; });
};

// KISOFT: Chat da equipe (pagina do painel). Badge de nao lidas + AVISO (som + notificacao do
// navegador) quando chega mensagem nova e LEMBRETE a cada 30s enquanto houver nao lida.
// Nao avisa se o Chat esta aberto e a aba visivel (a propria pagina marca como lido).
const CHAT_ITEM = { label: 'Chat equipe', icon: 'tchat', view: 'chat', kind: 'page', key: 'chat' };
const chatUnread = ref(0);
let chatTimer = null;
let chatUltimoAvisado = 0; // maior id de mensagem ja avisado
let chatUltimoAviso = 0; // epoch do ultimo aviso (base do lembrete de 30s)
let audioCtx = null;
// KISOFT: sons do aviso do chat. A preferencia (som/volume/lembrete) e POR USUARIO e vem do
// painel junto com o poll de nao lidas (chat_unread -> aviso); a tela de ajuste fica no Chat equipe.
const SONS = {
  sino: { notas: [[1318, 0, 1.2, 'sine', 1], [1976, 0, 0.85, 'sine', 0.5], [2637, 0, 0.5, 'sine', 0.22], [659, 0.03, 1.5, 'sine', 0.75]] },
  duplo: { notas: [[880, 0, 0.3, 'sine', 1], [1175, 0.14, 0.34, 'sine', 1]] },
  alerta: { notas: [[784, 0, 0.16, 'square', 0.55], [988, 0.17, 0.16, 'square', 0.55], [1319, 0.34, 0.4, 'square', 0.6]] },
  pop: { notas: [[320, 0, 0.13, 'triangle', 1], [880, 0.02, 0.12, 'sine', 0.6]] },
  digital: { notas: [[1500, 0, 0.08, 'square', 0.5], [1500, 0.11, 0.08, 'square', 0.5], [2100, 0.22, 0.16, 'square', 0.55]] },
  sirene: { sweep: true },
  mudo: { notas: [] },
};
const chatAviso = ref({ som: 'sino', vol: 80, lembrete: 30 });
const chatBeep = () => {
  try {
    if (!audioCtx) return;
    if (audioCtx.state === 'suspended') audioCtx.resume();
    const nome = chatAviso.value.som || 'sino';
    const mx = (Math.max(0, Math.min(100, Number(chatAviso.value.vol))) / 100) * 0.9;
    if (!mx || nome === 'mudo') return;
    const cfg = SONS[nome] || SONS.sino;
    if (cfg.sweep) {
      const o = audioCtx.createOscillator();
      const g = audioCtx.createGain();
      const t = audioCtx.currentTime;
      o.type = 'triangle';
      o.connect(g);
      g.connect(audioCtx.destination);
      o.frequency.setValueAtTime(620, t);
      o.frequency.linearRampToValueAtTime(1180, t + 0.22);
      o.frequency.linearRampToValueAtTime(620, t + 0.44);
      o.frequency.linearRampToValueAtTime(1180, t + 0.66);
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(mx, t + 0.04);
      g.gain.setValueAtTime(mx, t + 0.6);
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.8);
      o.start(t);
      o.stop(t + 0.82);
      return;
    }
    (cfg.notas || []).forEach(([freq, delay, dur, forma, peso]) => {
      const o = audioCtx.createOscillator();
      const g = audioCtx.createGain();
      const t = audioCtx.currentTime + delay;
      o.type = forma;
      o.frequency.value = freq;
      o.connect(g);
      g.connect(audioCtx.destination);
      g.gain.setValueAtTime(0.0001, t);
      g.gain.exponentialRampToValueAtTime(Math.max(0.0002, mx * peso), t + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
      o.start(t);
      o.stop(t + dur + 0.02);
    });
  } catch (e) {
    // sem audio (navegador bloqueou) -> segue so com a notificacao
  }
};const chatAvisar = (titulo, corpo) => {
  chatBeep();
  if ('Notification' in window && Notification.permission === 'granted') {
    try {
      const n = new Notification(titulo, { body: corpo, tag: 'kisoft-chat-equipe', renotify: true });
      n.onclick = () => {
        window.focus();
        emit('select', CHAT_ITEM);
        n.close();
      };
    } catch (e) {
      // alguns navegadores nao deixam criar Notification fora de service worker
    }
  }
};
const pollChat = () => {
  const tok = (currentUser.value && currentUser.value.access_token) || '';
  if (!tok) return;
  fetch('https://kisoft.com.br/tarefass/index.php?chat_unread=1&cwtok=' + encodeURIComponent(tok), { credentials: 'include', cache: 'no-store' })
    .then(r => r.json())
    .then(d => {
      const n = d && d.allowed && d.unread ? d.unread : 0;
      if (d && d.aviso) chatAviso.value = d.aviso;
      chatUnread.value = n;
      if (!n) {
        chatUltimoAviso = 0;
        return;
      }
      if (props.view === 'chat' && document.visibilityState === 'visible') return;
      const agora = Date.now();
      if (d.last_id > chatUltimoAvisado) {
        chatUltimoAvisado = d.last_id;
        chatUltimoAviso = agora;
        chatAvisar('Chat equipe · ' + (d.last_autor || 'Equipe'), d.last_texto || 'Nova mensagem');
      } else if (Number(chatAviso.value.lembrete) > 0 && agora - chatUltimoAviso >= Number(chatAviso.value.lembrete) * 1000) {
        chatUltimoAviso = agora;
        chatAvisar('Chat equipe', n > 1 ? n + ' mensagens não lidas' : '1 mensagem não lida');
      }
    })
    .catch(() => {});
};
// audio e permissao de notificacao so podem ser liberados apos um clique do usuario
const liberarAviso = () => {
  try {
    if (!audioCtx) audioCtx = new (window.AudioContext || window.webkitAudioContext)();
    else if (audioCtx.state === 'suspended') audioCtx.resume();
  } catch (e) {
    audioCtx = null;
  }
  if ('Notification' in window && Notification.permission === 'default') {
    try {
      Notification.requestPermission();
    } catch (e) {
      // ignore
    }
  }
};

onMounted(() => {
  pollCal();
  loadPerms();
  pollChat();
  pollExec();
  calTimer = setInterval(() => { pollCal(); pollExec(); }, 10000);
  chatTimer = setInterval(pollChat, 5000);
  document.addEventListener('pointerdown', liberarAviso);
});
onBeforeUnmount(() => {
  clearInterval(calTimer);
  clearInterval(chatTimer);
  document.removeEventListener('pointerdown', liberarAviso);
});

// paths de icone EXATOS do painel
const IC = {
  dash: 'M3 3h8v8H3zM13 3h8v5h-8zM13 10h8v11h-8zM3 13h8v8H3z',
  layers: 'M12 2 2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5',
  folder: 'M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z',
  check: 'M9 6h11M9 12h11M9 18h11M4 6h.01M4 12h.01M4 18h.01',
  target: 'M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20zM12 7a5 5 0 1 0 0 10 5 5 0 0 0 0-10zM12 11a1 1 0 1 0 0 2 1 1 0 0 0 0-2z',
  users: 'M17 21v-2a4 4 0 0 0-4-4H7a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z',
  flag: 'M4 22V4a1 1 0 0 1 1-1h11l-2 3 2 3H5',
  badge: 'M9 12l2 2 4-4M12 2 4 5v6c0 5 3.5 8 8 9 4.5-1 8-4 8-9V5l-8-3z',
  cal: 'M3 4.5h18v16H3zM3 9.5h18M8 2.5v4M16 2.5v4',
  mega: 'M4 10v4a1 1 0 0 0 1 1h3l5 4V5L8 9H5a1 1 0 0 0-1 1zM17 8a5 5 0 0 1 0 8',
  ticket: 'M4 5h16a1 1 0 0 1 1 1v3a2 2 0 0 0 0 4v3a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1v-3a2 2 0 0 0 0-4V6a1 1 0 0 1 1-1zM14 5v14',
  circle: 'M9 11l2 2 4-4M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0z',
  activity: 'M22 12h-4l-3 9L9 3l-3 9H2',
  bar: 'M4 20V10M10 20V4M16 20v-7M22 20H2',
  layout: 'M3 3h18v18H3zM3 9h18M9 21V9',
  clock: 'M3 3v5h5M3.05 13A9 9 0 1 0 6 5.3L3 8M12 7v5l4 2',
  shield: 'M12 22c5-1 8-4 8-9V5l-8-3-8 3v8c0 5 3 8 8 9zM9.5 11.5a2.5 2.5 0 0 1 5 0V13M9 13h6v3H9z',
  gear: 'M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zM19 12a7 7 0 0 0-.1-1l2-1.6-2-3.4-2.3 1a7 7 0 0 0-1.7-1L14.5 2h-4l-.4 2.9a7 7 0 0 0-1.7 1l-2.3-1-2 3.4L4 11a7 7 0 0 0 0 2l-2 1.6 2 3.4 2.3-1a7 7 0 0 0 1.7 1l.4 2.9h4l.4-2.9a7 7 0 0 0 1.7-1l2.3 1 2-3.4L18.9 13a7 7 0 0 0 .1-1z',
  funnel: 'M3 4h18l-7 8v6l-4 2v-8z',
  chat: 'M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z',
  tree: 'M9 3h6v4H9zM3 17h6v4H3zM15 17h6v4h-6zM12 7v4M6 17v-3h12v3M12 14v-3',
  card: 'M2 8a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2zM2 10h20M6 15h4',
  tchat: 'M14 9a2 2 0 0 1-2 2H6l-4 4V4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2zM18 9h2a2 2 0 0 1 2 2v11l-4-4h-6a2 2 0 0 1-2-2v-1',
  trend: 'M3 17l6-6 4 4 8-8M14 7h7v7',
};
// cor de cada icone EXATA do painel
const ICO = {
  card: '#2dd4bf', layout: '#5b8cff', dash: '#22c55e', layers: '#8b5cf6', funnel: '#0ea5e9',
  chat: '#38bdf8', tree: '#14b8a6', folder: '#f59e0b', check: '#3b82f6', ticket: '#ec4f26',
  target: '#e5484d', users: '#a855f7', mega: '#f2557a', flag: '#6366f1', badge: '#eab308',
  cal: '#0891b2', circle: '#10b981', activity: '#f97316', bar: '#3b82f6', clock: '#94a3b8',
  gear: '#64748b', shield: '#22c55e', tchat: '#a78bfa', trend: '#e879f9',
};
// secoes EXATAS do painel. view = pagina; kind: atendimento | page(migrada) | soon(nao migrada)
const SECTIONS = [
  // Meta fica sozinha no topo, sem titulo de categoria
  { name: '', items: [
    { label: 'Meta', icon: 'layout', view: 'home', kind: 'page', key: 'home' },
  ] },
  { name: 'Atendimento', items: [
    { label: 'Atendimento', icon: 'chat', view: 'atendimento', kind: 'atendimento', key: 'atendimento' },
    CHAT_ITEM,
  ] },
  { name: 'Comercial', items: [
    { label: 'CRM', icon: 'funnel', view: 'crm', kind: 'page', key: 'crm' },
    { label: 'Assinaturas', icon: 'card', view: 'assinaturas', kind: 'page', key: 'assinaturas' },
  ] },
  { name: 'Trabalho', items: [
    { label: 'Projetos', icon: 'folder', view: 'projetos', kind: 'page', key: 'projetos' },
    { label: 'Tarefas', icon: 'check', view: 'list', kind: 'page', key: 'list' },
    { label: 'Minhas Tarefas', icon: 'circle', view: 'minhas', kind: 'page', key: 'minhas' },
    { label: 'Calendário', icon: 'cal', view: 'calendar', kind: 'page', key: 'calendar' },
    { label: 'Chamados', icon: 'ticket', view: 'chamados', kind: 'page', key: 'chamados' },
    { label: 'Aprovações', icon: 'badge', view: 'approvals', kind: 'soon', key: 'approvals' },
  ] },
  { name: 'Empresa', items: [
    { label: 'Organograma', icon: 'tree', view: 'organo', kind: 'page', key: 'organograma' },
    { label: 'Reuniões', icon: 'users', view: 'reunioes', kind: 'page', key: 'reunioes' },
    { label: 'Decisões', icon: 'flag', view: 'decisoes', kind: 'soon', key: 'decisoes' },
    { label: 'Calendário Editorial', icon: 'mega', view: 'editorial', kind: 'soon', key: 'editorial' },
  ] },
  { name: 'Indicadores', items: [
    { label: 'Visão Executiva', icon: 'dash', view: 'exec', kind: 'page', key: 'exec' },
    { label: 'Métricas', icon: 'trend', view: 'metricas', kind: 'page', key: 'metricas' },
    { label: 'Áreas da Empresa', icon: 'layers', view: 'areas', kind: 'page', key: 'areas' },
    { label: 'Metas & KPIs', icon: 'target', view: 'metas', kind: 'soon', key: 'metas' },
  ] },
  { name: 'Operação', items: [
    { label: 'Controle de ponto', icon: 'clock', view: 'ponto', kind: 'soon', key: 'ponto' },
    { label: 'Atividades', icon: 'activity', view: 'history', kind: 'page', key: 'atividades' },
  ] },
  { name: 'Relatórios', items: [
    { label: 'Relatórios', icon: 'bar', view: 'relatorios', kind: 'soon', key: 'relatorios' },
    { label: 'Auditoria de Pagamentos', icon: 'ticket', view: 'pagamentos', kind: 'page', key: 'pagamentos' },
    { label: 'Dashboards', icon: 'layout', view: 'dashboards', kind: 'soon', key: 'dashboards' },
    { label: 'Histórico', icon: 'clock', view: 'history', kind: 'soon', key: 'historico' },
  ] },
  { name: 'Configurações', items: [
    { label: 'Config. Gestão', icon: 'gear', view: 'gcfg', kind: 'soon', key: 'gcfg' },
    { label: 'Equipe', icon: 'users', view: 'config', kind: 'page', key: 'config' },
    { label: 'Permissões', icon: 'shield', view: 'permissoes', kind: 'soon', key: 'permissoes' },
  ] },
];
// Atendimento (Chatwoot) sempre visivel; o resto so se a config do usuario liberar aquela pagina.
const canShow = it => it.kind === 'atendimento' || (allowedKeys.value !== null && allowedKeys.value.includes(it.key));
const visibleSections = computed(() =>
  SECTIONS.map(s => ({ name: s.name, items: s.items.filter(canShow) })).filter(s => s.items.length)
);
</script>

<template>
  <aside class="side" :class="{ min: recolhida }">
    <div class="brand">
      <div class="logo">
        <svg viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" style="display:block;width:100%;height:100%">
          <defs><linearGradient id="klg" x1="4" y1="2" x2="44" y2="46" gradientUnits="userSpaceOnUse"><stop stop-color="#ffbf42" /><stop offset=".5" stop-color="#f97b2c" /><stop offset="1" stop-color="#ec4f26" /></linearGradient></defs>
          <rect x="1" y="1" width="46" height="46" rx="13" fill="url(#klg)" />
          <rect x="1.75" y="1.75" width="44.5" height="44.5" rx="12.25" fill="none" stroke="#fff" stroke-opacity=".22" stroke-width="1.5" />
          <path d="M18 12.5v23M18 24l12.5-11.5M18 24l13 11.5" fill="none" stroke="#fff" stroke-width="4.6" stroke-linecap="round" stroke-linejoin="round" />
          <circle cx="34.5" cy="13.5" r="2.4" fill="#fff" />
        </svg>
      </div>
      <div class="brand-tx"><b>Kisoft</b><small>Gestão da Empresa</small></div>
      <!-- pin (mesmo icone do painel da direita): ligado = fixada aberta; desligado = abre/fecha com o mouse -->
      <button v-if="!recolhida" type="button" class="kg-pin" :class="{ on: fixada }" :title="fixada ? 'Desafixar (a barra recolhe quando o mouse sair)' : 'Fixar a barra aberta'" @click="emit('toggle')">
        <svg viewBox="0 0 24 24"><path d="M12 17v5M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H8a2 2 0 0 0 0 4 1 1 0 0 1 1 1z" /></svg>
      </button>
    </div>
    <!-- recolhida: painel com seta pra direita = abrir -->
    <!-- recolhida: passar o mouse AQUI abre a barra (fecha ao sair dela); clicar fixa aberta -->
    <button v-if="recolhida" type="button" class="kg-open" title="Abrir menu (clique para fixar)" @mouseenter="emit('abrir')" @click="emit('toggle')">
      <svg viewBox="0 0 24 24"><path d="M4 4h16a1 1 0 0 1 1 1v14a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1zM9 4v16M13 9l3 3-3 3" /></svg>
    </button>
    <nav class="nav">
      <!-- visibleSections = so as paginas liberadas p/ este usuario (config users.perms) -->
      <template v-for="sec in visibleSections" :key="sec.name || 'topo'">
        <div v-if="sec.name" class="sec"><span>{{ sec.name }}</span></div>
        <a
          v-for="it in sec.items"
          :key="it.label"
          :class="{ on: it.view === view, 'kg-dest': it.view === 'atendimento' }"
          :title="recolhida ? it.label : null"
          @click="emit('select', it)"
        >
          <svg viewBox="0 0 24 24" :style="{ color: ICO[it.icon] || 'currentColor' }"><path :d="IC[it.icon]" /></svg><span class="lbl">{{ it.label }}</span>
          <span v-if="it.view === 'exec' && execBelow > 0" class="kg-nbadge" title="Indicadores abaixo da meta">↓{{ execBelow }}</span>
          <span v-else-if="it.view === 'calendar' && calCount > 0" class="kg-nbadge">{{ calCount }}</span>
          <span v-else-if="it.view === 'atendimento' && responderCount > 0" class="kg-nbadge">{{ responderCount }}</span>
          <span v-else-if="it.view === 'chat' && chatUnread > 0" class="kg-nbadge">{{ chatUnread }}</span>
        </a>
      </template>
    </nav>
    <div class="dica">
      <div class="dica-h"><span class="dica-ic">💡</span><b>Dica do dia</b></div>
      <p>Acompanhe seus indicadores diariamente e tome decisões com base em dados.</p>
    </div>
    <div class="foot foot-exit" :title="recolhida ? 'Voltar ao Chatwoot' : null" @click="emit('exit')">
      <svg viewBox="0 0 24 24"><path d="M9 14 4 9l5-5M4 9h11a5 5 0 0 1 0 10h-1" /></svg><span class="lbl">Voltar ao Chatwoot</span>
    </div>
  </aside>
</template>

<style>
/* CSS EXATO do painel (index.php :root ~3205 + html.tema-light: sidebar navy #0f1c3d) */
.kg-rail{--panel2:#0f1c3d;--accent:#f2662e;
  --sec:#5b6472;--ntx:#b6c0d0;--sideline:rgba(255,255,255,.08);--sidehov:rgba(255,255,255,.06);--sidemut:#8791a3;
  background:#0f1c3d;height:100vh;overflow:hidden}
.kg-rail .side{width:220px;flex-shrink:0;background:var(--panel2);border-right:1px solid var(--sideline);display:flex;flex-direction:column;height:100vh;overflow-y:auto;scrollbar-width:none;-ms-overflow-style:none;font-family:'Inter',system-ui,-apple-system,Arial,sans-serif;font-size:13px}
.kg-rail .side::-webkit-scrollbar{width:0;height:0;display:none}
.kg-rail .brand{display:flex;align-items:center;gap:11px;padding:16px 18px;border-bottom:1px solid var(--sideline)}
.kg-rail .brand .logo{width:40px;height:40px;flex-shrink:0}
.kg-rail .brand b{font-size:14px;font-weight:700;display:block;line-height:1.1;color:#f4f6fb}
.kg-rail .brand small{font-size:11px;color:var(--sidemut)}
.kg-rail .nav{padding:8px 10px;flex:1}
.kg-rail .nav .sec{font-size:10px;font-weight:600;letter-spacing:.08em;color:var(--sec);text-transform:uppercase;padding:14px 10px 6px}
.kg-rail .nav a{display:flex;align-items:center;gap:11px;padding:8px 11px;border-radius:9px;color:var(--ntx);font-weight:400;font-size:12.5px;margin:1px 0;cursor:pointer;user-select:none}
.kg-rail .nav a svg{width:18px;height:18px;stroke:currentColor;fill:none;stroke-width:1.9;stroke-linecap:round;stroke-linejoin:round;flex-shrink:0}
.kg-rail .nav a:hover{background:var(--sidehov);color:#fff}
.kg-rail .nav a.on{background:rgba(242,102,46,.16);color:var(--accent);font-weight:600}
/* KISOFT: Atendimento e o UNICO item com plano/fundo proprio no icone (margem negativa mantem o alinhamento) */
.kg-rail .nav a.kg-dest svg{width:26px;height:26px;padding:4px;margin:-4px;box-sizing:border-box;border-radius:8px;
  background:linear-gradient(135deg,#38bdf8,#2563eb);stroke:#fff}
.kg-rail .nav a.kg-dest:hover svg{filter:brightness(1.08)}
/* KISOFT: badge de contagem (padrao .nb-seg red do painel) */
.kg-rail .nav a .kg-nbadge{margin-left:auto;font-size:10.5px;font-weight:700;border-radius:20px;padding:1px 7px;background:rgba(248,81,73,.16);color:#f85149;white-space:nowrap;flex-shrink:0}
.kg-rail .dica{margin:8px 12px 6px;background:rgba(59,130,246,.15);border:1px solid rgba(59,130,246,.28);border-radius:11px;padding:12px 13px}
.kg-rail .dica-h{display:flex;align-items:center;gap:8px;font-size:12.5px;color:#eaf1ff;margin-bottom:6px}
.kg-rail .dica-h b{font-weight:700}.kg-rail .dica-ic{font-size:14px}
.kg-rail .dica p{font-size:11px;color:#aeb9d3;line-height:1.5;margin:0}
.kg-rail .foot{padding:12px 16px;border-top:1px solid var(--sideline);color:var(--sidemut);font-size:12.5px;display:flex;align-items:center;gap:8px;cursor:pointer}
.kg-rail .foot:hover{color:#fff}
/* KISOFT: recolher/abrir no TOPO */
.kg-rail .side:not(.min) .brand{padding-right:12px;gap:10px}
.kg-rail .brand-tx{min-width:0;white-space:nowrap;overflow:hidden}
.kg-rail .brand-tx small{display:block;overflow:hidden;text-overflow:ellipsis}
.kg-rail .kg-pin{margin-left:auto;width:28px;height:28px;flex-shrink:0;display:flex;align-items:center;justify-content:center;border-radius:7px;border:0;background:transparent;color:#8791a3;cursor:pointer;transition:background .15s,color .15s}
.kg-rail .kg-pin:hover{background:rgba(255,255,255,.12);color:#fff}
.kg-rail .kg-pin.on{background:rgba(255,255,255,.2);color:#fff}
.kg-rail .kg-open{display:flex;align-items:center;justify-content:center;margin:10px auto 2px;width:40px;height:34px;border-radius:9px;border:0;background:rgba(255,255,255,.08);color:#b6c0d0;cursor:pointer;transition:background .15s,color .15s}
.kg-rail .kg-open:hover{background:rgba(242,102,46,.16);color:var(--accent)}
.kg-rail .kg-pin svg,.kg-rail .kg-open svg{stroke:currentColor;fill:none;stroke-width:2;stroke-linecap:round;stroke-linejoin:round}
.kg-rail .kg-pin svg{width:15px;height:15px}
.kg-rail .kg-open svg{width:19px;height:19px}
.kg-rail .foot svg{width:16px;height:16px;stroke:currentColor;fill:none;stroke-width:2;stroke-linecap:round;stroke-linejoin:round;flex-shrink:0}
/* KISOFT: modo RECOLHIDO (so icones, 64px) */
.kg-rail .side{transition:width .22s ease}
.kg-rail .nav a,.kg-rail .foot{white-space:nowrap;overflow:hidden}
.kg-rail .side.min{width:64px}
.kg-rail .side.min .brand{justify-content:center;padding:16px 0}
.kg-rail .side.min .brand-tx,.kg-rail .side.min .lbl,.kg-rail .side.min .dica{display:none}
.kg-rail .side.min .nav{padding:4px 8px 8px}
/* KISOFT: separa a setinha de abrir do primeiro item (Meta), que nao tem titulo de categoria */
.kg-rail .side.min .kg-open{margin:10px auto 9px}
.kg-rail .side.min .nav{border-top:1px solid var(--sideline);margin:0 8px;padding:9px 0 8px}
.kg-rail .side.min .nav .sec{padding:8px 6px 4px}
.kg-rail .side.min .nav .sec span{display:none}
.kg-rail .side.min .nav .sec::after{content:"";display:block;height:1px;background:var(--sideline)}
.kg-rail .side.min .nav a{justify-content:center;padding:9px 0;position:relative;overflow:visible}
.kg-rail .side.min .nav a .kg-nbadge{position:absolute;top:1px;right:1px;margin:0;padding:0 5px;font-size:9.5px;line-height:15px;background:#f85149;color:#fff}
.kg-rail .side.min .foot{justify-content:center;padding:12px 0}
</style>
